import {isNil} from 'ramda';

import type {ChartReportDocument} from './ChartReportDocument';
import type {createReportDocumentTables} from './createReportDocumentTables';
import {formatReportDocumentValue} from './formatReportDocumentValue';

type DocumentTable = ReturnType<typeof createReportDocumentTables>[number];

// Conservative point estimates for 7pt cells within the A4 content width.
const TABLE_WIDTH = 530;
const TABLE_CATEGORY_WIDTH = 55;
// Match the category-cell max-width in REPORT_DOCUMENT_STYLES.
const TABLE_CATEGORY_MAX_WIDTH = 168;
const TABLE_CHARACTER_WIDTH = 3.9;
const TABLE_CELL_PADDING = 8;

export function splitReportDocumentTableColumns(
  table: DocumentTable,
  document: ChartReportDocument
) {
  const groups: number[][] = [];
  let indices: number[] = [];
  const categoryWidth = Math.min(
    TABLE_CATEGORY_MAX_WIDTH,
    Math.max(
      TABLE_CATEGORY_WIDTH,
      ...table.rows.map((row) => row.category.length * TABLE_CHARACTER_WIDTH)
    ) + TABLE_CELL_PADDING
  );
  let width = categoryWidth;
  table.columns.forEach((column, columnIndex) => {
    const values = table.rows.map((row) =>
      formatReportDocumentValue(row.values[columnIndex], column, document, {
        table: true,
        percentage: column.percentages ? (column.percentages[row.index] ?? null) : undefined,
      })
    );
    const summary = column.summary;
    if (!isNil(summary)) {
      values.push(
        formatReportDocumentValue(summary.value, summary, document, {
          table: true,
          percentage: summary.percentage,
        })
      );
    }
    const columnWidth =
      Math.max(...values.map((value) => value.length), 4) * TABLE_CHARACTER_WIDTH +
      TABLE_CELL_PADDING;
    if (indices.length > 0 && width + columnWidth > TABLE_WIDTH) {
      groups.push(indices);
      indices = [];
      width = categoryWidth;
    }
    indices.push(columnIndex);
    width += columnWidth;
  });
  if (indices.length > 0) {
    groups.push(indices);
  }
  return groups;
}
